import { msg } from "@lingui/macro";
import type { IntakeData } from "../lib/types";
import { i18n } from "@/i18n";
import {
  formatOptionSelection,
  type IntakeOptionLocale,
} from "../lib/intakeOptionMaps";
import { formatRegionCodeForLocale } from "../lib/regionDisplay";
import type { ProcedureType } from "../lib/specialtyTags";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function reviewOptionLocale(): IntakeOptionLocale {
  return i18n.locale === "es" ? "es" : "en";
}

export function getProcedureLabel(p: IntakeData["procedures"]): string {
  if (typeof p === "string") {
    return p === "other" ? "Other specialty" : p;
  }
  const o = p as ProcedureType | undefined;
  return o?.name || o?.slug || "Other specialty";
}

/** Same as `getProcedureLabel` but localized for on-screen review (emails stay English). */
function getProcedureLabelForUi(p: IntakeData["procedures"]): string {
  if (typeof p === "string") {
    return p === "other" ? i18n._(msg`Other specialty`) : p;
  }
  const o = p as ProcedureType | undefined;
  return o?.name || o?.slug || i18n._(msg`Other specialty`);
}

export function formatOtherSpecialtyInquiryPlain(data: IntakeData): string {
  const lines: string[] = [];
  lines.push("OTHER SPECIALTY INQUIRY (Intake v2)");
  lines.push("=====================================");
  lines.push("");

  lines.push("CONTACT");
  lines.push(
    `Name: ${(data.firstName ?? "").trim()} ${(data.lastName ?? "").trim()}`.trim(),
  );
  lines.push(`Email: ${(data.email ?? "").trim()}`);
  lines.push(`Phone: ${(data.phone ?? "").trim()}`);
  lines.push(`Country: ${(data.country ?? "").trim()}`);
  if (data.preferredLanguage) {
    lines.push(
      `Preferred language: ${data.preferredLanguage === "es" ? "Español" : "English"}`,
    );
  }
  lines.push("");

  lines.push("SPECIALTY");
  lines.push(`Selected: ${getProcedureLabel(data.procedures)}`);
  lines.push("");

  lines.push("OTHER SPECIALTY DETAILS");
  lines.push(
    `Medical need: ${formatOptionSelection(
      "otherSpecialtyFocus",
      data.otherSpecialtyFocus,
      "en",
    )}`,
  );
  lines.push(
    `Body area(s): ${formatOptionSelection(
      "otherSpecialtyBodyAreas",
      data.otherSpecialtyBodyAreas,
      "en",
    )}`,
  );
  lines.push(
    `Symptoms / context: ${formatOptionSelection(
      "otherSpecialtySymptoms",
      data.otherSpecialtySymptoms,
      "en",
    )}`,
  );
  lines.push(
    `Timeline: ${formatOptionSelection(
      "otherSpecialtyTimeline",
      data.otherSpecialtyTimeline,
      "en",
    )}`,
  );
  const more = (data.otherSpecialtyAdditionalInfo ?? "").trim();
  lines.push(`Additional information: ${more || "—"}`);
  lines.push("");
  lines.push("---");
  lines.push(
    "This message was generated from the JEMA general intake form (other specialty flow).",
  );

  return lines.join("\n");
}

function row(label: string, value: string): string {
  const v = value.trim() || "—";
  return `
  <tr>
    <td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;font-size:13px;color:#6b7280;width:38%;vertical-align:top;">${escapeHtml(label)}</td>
    <td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;font-size:14px;color:#111827;font-weight:500;vertical-align:top;">${escapeHtml(v)}</td>
  </tr>`;
}

/**
 * HTML email with inline styles (email-client safe). All user content is escaped.
 */
export function buildOtherSpecialtyInquiryHtml(data: IntakeData): string {
  const name =
    `${(data.firstName ?? "").trim()} ${(data.lastName ?? "").trim()}`.trim() ||
    "—";
  const email = (data.email ?? "").trim() || "—";
  const phone = (data.phone ?? "").trim() || "—";
  const country = (data.country ?? "").trim() || "—";
  const lang = data.preferredLanguage
    ? data.preferredLanguage === "es"
      ? "Español"
      : "English"
    : null;

  const rows: string[] = [];
  rows.push(row("Name", name));
  rows.push(row("Email", email));
  rows.push(row("Phone", phone));
  rows.push(row("Country", country));
  if (lang) rows.push(row("Preferred language", lang));
  rows.push(row("Specialty", getProcedureLabel(data.procedures)));
  rows.push(
    row(
      "Medical need",
      formatOptionSelection("otherSpecialtyFocus", data.otherSpecialtyFocus, "en"),
    ),
  );
  rows.push(
    row(
      "Body area(s)",
      formatOptionSelection(
        "otherSpecialtyBodyAreas",
        data.otherSpecialtyBodyAreas,
        "en",
      ),
    ),
  );
  rows.push(
    row(
      "Symptoms / condition",
      formatOptionSelection(
        "otherSpecialtySymptoms",
        data.otherSpecialtySymptoms,
        "en",
      ),
    ),
  );
  rows.push(
    row(
      "Preferred timeline",
      formatOptionSelection(
        "otherSpecialtyTimeline",
        data.otherSpecialtyTimeline,
        "en",
      ),
    ),
  );
  rows.push(
    row(
      "Additional notes",
      (data.otherSpecialtyAdditionalInfo ?? "").trim() || "—",
    ),
  );

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Other specialty inquiry</title>
</head>
<body style="margin:0;padding:0;background-color:#f3f4f6;font-family:'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
  <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="background-color:#f3f4f6;padding:24px 12px;">
    <tr>
      <td align="center">
        <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="max-width:600px;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(22,107,147,0.12);border:1px solid #e5e7eb;">
          <tr>
            <td style="background:linear-gradient(135deg,#166B93 0%,#12587A 100%);padding:24px 28px;">
              <p style="margin:0;font-size:11px;font-weight:700;letter-spacing:0.08em;color:rgba(255,255,255,0.85);text-transform:uppercase;">JEMA · Intake</p>
              <h1 style="margin:8px 0 0;font-size:22px;line-height:1.25;color:#ffffff;font-weight:700;">Other specialty inquiry</h1>
              <p style="margin:10px 0 0;font-size:14px;line-height:1.5;color:rgba(255,255,255,0.9);">Submitted from the general intake form (other specialty flow).</p>
            </td>
          </tr>
          <tr>
            <td style="padding:0;">
              <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="border-collapse:collapse;">
                ${rows.join("")}
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding:16px 24px 24px;background:#f9fafb;border-top:1px solid #e5e7eb;">
              <p style="margin:0;font-size:12px;line-height:1.5;color:#6b7280;">This message was generated automatically. Reply to the patient using the email address listed above.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/** Rows for the other-specialty review screen (same labels as email). */
export function getOtherSpecialtyReviewRows(
  data: IntakeData,
): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];

  rows.push({
    label: i18n._(msg`Name`),
    value:
      `${(data.firstName ?? "").trim()} ${(data.lastName ?? "").trim()}`.trim() ||
      "—",
  });
  rows.push({
    label: i18n._(msg`Email`),
    value: (data.email ?? "").trim() || "—",
  });
  rows.push({
    label: i18n._(msg`Phone`),
    value: (data.phone ?? "").trim() || "—",
  });
  rows.push({
    label: i18n._(msg`Country`),
    value:
      formatRegionCodeForLocale((data.country ?? "").trim(), i18n.locale) ||
      (data.country ?? "").trim() ||
      "—",
  });
  if (data.preferredLanguage) {
    rows.push({
      label: i18n._(msg`Preferred language`),
      value: data.preferredLanguage === "es" ? "Español" : "English",
    });
  }

  rows.push({
    label: i18n._(msg`Specialty`),
    value: getProcedureLabelForUi(data.procedures) || "—",
  });

  const loc = reviewOptionLocale();
  rows.push({
    label: i18n._(msg`Medical need`),
    value:
      formatOptionSelection("otherSpecialtyFocus", data.otherSpecialtyFocus, loc) ||
      "—",
  });
  rows.push({
    label: i18n._(msg`Body area(s)`),
    value:
      formatOptionSelection(
        "otherSpecialtyBodyAreas",
        data.otherSpecialtyBodyAreas,
        loc,
      ) || "—",
  });
  rows.push({
    label: i18n._(msg`Symptoms / condition`),
    value:
      formatOptionSelection(
        "otherSpecialtySymptoms",
        data.otherSpecialtySymptoms,
        loc,
      ) || "—",
  });
  rows.push({
    label: i18n._(msg`Preferred timeline`),
    value:
      formatOptionSelection(
        "otherSpecialtyTimeline",
        data.otherSpecialtyTimeline,
        loc,
      ) || "—",
  });
  rows.push({
    label: i18n._(msg`Additional notes`),
    value: (data.otherSpecialtyAdditionalInfo ?? "").trim() || "—",
  });

  return rows;
}
